/* ==========================================================================
   Southern Legends CRO draft (2026-09)
   Progressive enhancements layered on top of the Flex theme's own cart,
   product and collection scripts. Native Shopify Ajax endpoints only; no
   third-party libraries. Flex keeps ownership of add to cart, line updates
   and removal, so app hooks (Globo pre-order, Afterpay, tracking) still run.
   ========================================================================== */
(function () {
  'use strict';

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function focusables(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; }
    );
  }

  function flexCart() {
    return window.Shopify && Shopify.theme && Shopify.theme.jsAjaxCart;
  }

  /* ------------------------------------------------------------------------
     1. Side-cart drawer: dialog semantics, focus handling, Escape, scroll lock
     ------------------------------------------------------------------------ */
  function initDrawer() {
    var drawer = document.querySelector('#theme-ajax-cart.is-drawer');
    if (!drawer) return;

    var lastTrigger = null;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-labelledby', 'cro-drawer-title');
    drawer.setAttribute('aria-hidden', 'true');

    document.querySelectorAll('[data-ajax-cart-trigger] > a').forEach(function (a) {
      a.setAttribute('aria-haspopup', 'dialog');
      a.setAttribute('aria-controls', 'theme-ajax-cart');
    });

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-ajax-cart-trigger], [data-add-to-cart-trigger], .ajax-submit, [data-cro-add]');
      if (trigger) lastTrigger = trigger.querySelector('a') || trigger;
    }, true);

    function isOpen() { return drawer.classList.contains('is-visible'); }

    function onOpen() {
      drawer.setAttribute('aria-hidden', 'false');
      if (typeof scanRecs === 'function') scanRecs();
      document.documentElement.classList.add('cro-scroll-lock');
      if (!drawer.contains(document.activeElement)) {
        window.setTimeout(function () {
          var close = drawer.querySelector('[data-cro-drawer-close]');
          if (close) close.focus({ preventScroll: true });
        }, 320);
      }
    }

    function onClose() {
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cro-scroll-lock');
      if (lastTrigger && document.contains(lastTrigger) && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus({ preventScroll: true });
      }
    }

    var wasOpen = isOpen();
    new MutationObserver(function () {
      var open = isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) onOpen(); else onClose();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        var cart = flexCart();
        if (cart && cart.hideDrawer) cart.hideDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(drawer);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (!drawer.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ------------------------------------------------------------------------
     2. Recommendations: only real, available products; module hidden when
        nothing relevant remains. Uses Shopify's recommendations endpoint.
     ------------------------------------------------------------------------ */
  var recCache = {};

  function fetchRecs(productId) {
    if (!recCache[productId]) {
      var url = root + 'recommendations/products?section_id=cro-recs&limit=10&product_id=' + encodeURIComponent(productId);
      recCache[productId] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }
    return recCache[productId];
  }

  function renderRecs(box) {
    if (box.hasAttribute('data-cro-loaded')) return;
    // Drawer recommendations only load once the drawer is actually open
    if (box.closest('#theme-ajax-cart') && !box.closest('#theme-ajax-cart.is-visible')) return;
    box.setAttribute('data-cro-loaded', '');
    var productId = box.getAttribute('data-product-id');
    if (!productId) return;
    var exclude = (box.getAttribute('data-exclude') || '').split(',').filter(Boolean);
    var limit = parseInt(box.getAttribute('data-limit') || '4', 10);
    var heading = box.getAttribute('data-heading') || 'You may also like';
    if (heading.toLowerCase() === 'complete the kit') heading = 'You may also like';

    fetchRecs(productId).then(function (html) {
      if (!html || !document.contains(box)) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var cards = Array.prototype.slice.call(tmp.querySelectorAll('.cro-rec'))
        .filter(function (card) { return exclude.indexOf(card.getAttribute('data-product-id')) === -1; })
        .slice(0, limit);
      if (!cards.length) {
        box.hidden = true;
        return;
      }
      var list = document.createElement('div');
      list.className = 'cro-recs__items';
      cards.forEach(function (c) { list.appendChild(c); });
      var head = document.createElement('div');
      head.className = 'cro-recs__head';
      var tag = box.classList.contains('cro-recs--drawer') ? 'p' : 'h2';
      head.innerHTML = '<' + tag + ' class="cro-recs__title"></' + tag + '>';
      head.firstChild.textContent = heading;
      box.innerHTML = '';
      box.appendChild(head);
      box.appendChild(list);
      box.hidden = false;
    });
  }

  function scanRecs() {
    document.querySelectorAll('[data-cro-recs]:not([data-cro-loaded])').forEach(renderRecs);
  }

  function initRecs() {
    scanRecs();
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () { pending = null; scanRecs(); }, 60);
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cro-add]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-cro-add');
      var card = btn.closest('.cro-rec, .cro-card');
      var old = card && card.querySelector('.cro-rec__msg');
      if (old) old.remove();
      btn.disabled = true;
      fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && (res.body.description || res.body.message) || 'Could not add this item.');
          btn.disabled = false;
          document.dispatchEvent(new CustomEvent('cro:cart-added', { detail: res.body }));
          if (document.querySelector('.cro-cart') && !btn.closest('#theme-ajax-cart')) {
            window.location.reload();
            return;
          }
          var cart = flexCart();
          if (cart && cart.updateView) {
            cart.updateView();
            if (cart.showDrawer) cart.showDrawer();
          } else {
            window.location.href = root + 'cart';
          }
        })
        .catch(function (err) {
          btn.disabled = false;
          if (!card) return;
          var msg = document.createElement('span');
          msg.className = 'cro-rec__msg';
          msg.setAttribute('role', 'alert');
          msg.textContent = err.message;
          (card.querySelector('.cro-rec__body, .cro-card__info') || card).appendChild(msg);
        });
    });
  }


  /* ------------------------------------------------------------------------
     3. Product page: live stock line and mobile purchase bar
     ------------------------------------------------------------------------ */
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Product gallery: "1 / N" counter, progress bar and zoom on top of Flex's Flickity */
  function initGallery(section) {
    var main = section.querySelector('.product-gallery__main');
    if (!main) return;
    var tries = 0;
    (function wait() {
      var $ = window.jQuery;
      var flkty = $ && $(main).data('flickity');
      if (!flkty) {
        if (tries++ < 40) window.setTimeout(wait, 150);
        return;
      }
      var total = flkty.slides.length;
      if (total < 2 && !main.querySelector('.product-gallery__link')) return;
      var meta = document.createElement('div');
      meta.className = 'cro-gal-meta';
      meta.innerHTML = (total > 1 ? '<span class="cro-gal-count" aria-live="polite"></span><span class="cro-gal-bar" aria-hidden="true"><i></i></span>' : '<span class="cro-gal-bar" aria-hidden="true" style="visibility:hidden"></span>') +
        (main.querySelector('.product-gallery__link') ? '<button type="button" class="cro-gal-zoom"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16"><path d="M1 6V1h5M10 1h5v5M15 10v5h-5M6 15H1v-5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>Zoom</button>' : '');
      var gallery = main.closest('.product-gallery') || main;
      gallery.parentNode.insertBefore(meta, gallery.nextSibling);
      var count = meta.querySelector('.cro-gal-count');
      var bar = meta.querySelector('.cro-gal-bar i');
      function update() {
        var i = flkty.selectedIndex;
        if (count) count.textContent = (i + 1) + ' / ' + total;
        if (bar && total > 1) {
          bar.style.width = (100 / total) + '%';
          bar.style.left = (100 / total * i) + '%';
        }
      }
      flkty.on('select', update);
      update();
      // Line the counter row up with the main image (thumbnails sit to its left on desktop)
      function align() {
        var offset = main.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
        meta.style.marginLeft = window.matchMedia('(min-width: 990px)').matches && offset > 0 ? offset + 'px' : '';
      }
      align();
      window.addEventListener('resize', align);
      var zoom = meta.querySelector('.cro-gal-zoom');
      if (zoom) zoom.addEventListener('click', function () {
        var link = main.querySelector('.is-selected .product-gallery__link') || main.querySelector('.product-gallery__link');
        if (link) link.click();
      });
      // Heights change once the new aspect-ratio CSS applies
      window.setTimeout(function () { flkty.resize(); }, 50);
    })();
  }

  function initPdp() {
    var section = document.querySelector('.cro-pdp');
    if (!section) return;
    initGallery(section);
    var form = section.querySelector('[data-product-form]');
    var selection = section.querySelector('[data-variant-selection]');
    var stock = section.querySelector('[data-cro-stock]');
    var inventory = [];
    try { inventory = JSON.parse((form && form.getAttribute('data-variant-inventory')) || '[]'); } catch (e) { inventory = []; }

    // Use Shopify's variant price in cents, not formatted price/range text.
    function updateAfterpay(variant) {
      var wrapper = section.querySelector('[data-cro-afterpay]');
      if (!wrapper) return;
      wrapper.hidden = !(variant && variant.available);
      var placement = wrapper.querySelector('square-placement');
      if (placement && variant) {
        placement.setAttribute('data-amount', (variant.price / 100).toFixed(2));
        placement.setAttribute('data-item-skus', variant.sku || '');
      }
    }

    function setStock(variant, state) {
      if (!stock) return;
      var threshold = parseInt(stock.getAttribute('data-threshold') || '3', 10);
      var cls = 'unavailable';
      var html = '';
      if (!variant) {
        html = state === 'not-selected' ? 'Choose an option to check availability' : 'This combination is unavailable';
      } else if (!variant.available) {
        cls = 'out';
        html = 'Sold out in this option';
      } else {
        var inv = inventory.filter(function (i) { return i.id === variant.id; })[0] || {};
        var managed = inv.inventory_management !== undefined ? inv.inventory_management : variant.inventory_management;
        var qty = inv.inventory_quantity;
        if (managed && qty > 0) {
          cls = 'in';
          html = 'In stock' + (qty <= threshold ? '<strong> · Only ' + escapeHtml(qty) + ' left</strong>' : '');
        } else if (!managed) {
          cls = 'available';
          html = 'Available';
        } else {
          cls = 'order';
          html = 'Available to order';
        }
      }
      stock.className = 'cro-stock cro-stock--' + cls;
      var text = stock.querySelector('[data-cro-stock-text]');
      if (text) text.innerHTML = html;
    }

    var sticky = document.querySelector('[data-cro-sticky]');
    var stickyBtn = sticky && sticky.querySelector('[data-cro-sticky-add]');
    // Flex / app scripts can re-render the purchase buttons, so always look up the live one
    function liveAddBtn() { return section.querySelector('[data-add-to-cart-trigger]'); }

    function updateSticky() {
      var addBtn = liveAddBtn();
      if (!sticky || !addBtn) return;
      var priceEl = section.querySelector('[data-price-ui] [data-price]');
      sticky.querySelector('[data-cro-sticky-price]').textContent = priceEl ? priceEl.textContent.trim() : '';
      var label = addBtn.querySelector('.text');
      stickyBtn.textContent = (label && label.textContent.trim()) || 'Add to cart';
    }

    if (selection) {
      selection.addEventListener('variant-change', function (e) {
        setStock(e.detail.variant, e.detail.state);
        updateAfterpay(e.detail.variant);
        section.setAttribute('data-cro-sale', String(!!(e.detail.variant && e.detail.variant.available && e.detail.variant.compare_at_price > e.detail.variant.price)));
        section.querySelectorAll('.sale-sticker').forEach(function (badge) {
          var variant = e.detail.variant;
          badge.hidden = !(variant && variant.available && variant.compare_at_price > variant.price);
        });
        window.setTimeout(updateSticky, 50);
      });
    }

    if (sticky && liveAddBtn()) {
      // Live at the top level of the page so no transformed/overflow ancestor can un-pin it
      if (sticky.parentNode !== document.body) document.body.appendChild(sticky);
      var phone = window.matchMedia('(max-width: 798px)');
      var shown = false;
      function setShown(show) {
        if (show === shown) return;
        shown = show;
        sticky.classList.toggle('is-visible', show);
        document.documentElement.classList.toggle('cro-sticky-on', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        stickyBtn.tabIndex = show ? 0 : -1;
        if (show) updateSticky();
      }
      function check() {
        sticky.hidden = !phone.matches;
        var btn = liveAddBtn();
        if (!phone.matches || !btn) { setShown(false); return; }
        var box = (btn.closest('.purchase-details') || btn).getBoundingClientRect();
        // Show once the real Add to cart has scrolled up past the top of the screen
        setShown(box.bottom < 0);
      }
      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { ticking = false; check(); });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      if (phone.addEventListener) phone.addEventListener('change', check);
      check();

      stickyBtn.addEventListener('click', function () {
        var addBtn = liveAddBtn();
        if (!addBtn) return;
        if (addBtn.disabled || addBtn.hasAttribute('data-options-unselected')) {
          var target = section.querySelector('[data-options-selection]') || addBtn;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var first = target.querySelector('[data-variant-option-value-wrapper] [tabindex="0"]');
          if (first) window.setTimeout(function () { first.focus({ preventScroll: true }); }, 400);
          return;
        }
        addBtn.click();
      });
      updateSticky();
    } else if (sticky) {
      sticky.remove();
    }
  }

  /* ------------------------------------------------------------------------
     4. Collection page (filled in below)
     ------------------------------------------------------------------------ */
  function submitClean(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (String(value).trim() !== '') params.append(key, value);
    });
    var qs = params.toString();
    window.location.href = form.getAttribute('action') + (qs ? '?' + qs : '');
  }

  function initCollection() {
    var col = document.querySelector('.cro-col');
    if (!col) return;

    // Sort
    var sort = col.querySelector('[data-cro-sort]');
    if (sort) sort.addEventListener('change', function () { submitClean(sort.form); });

    // Filters: desktop applies immediately, mobile panel applies on "Show results"
    var panel = col.querySelector('[data-cro-filters]');
    var form = col.querySelector('[data-cro-filter-form]');
    var openBtn = col.querySelector('[data-cro-filters-open]');
    var backdrop = col.querySelector('.cro-col__backdrop');
    var desktop = window.matchMedia('(min-width: 990px)');

    if (form) {
      form.addEventListener('change', function (e) {
        if (!desktop.matches) return;
        if (e.target.type === 'number') return;
        submitClean(form);
      });
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submitClean(form);
      });
      form.querySelectorAll('input[type="number"]').forEach(function (input) {
        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); submitClean(form); }
        });
        input.addEventListener('blur', function () {
          if (desktop.matches && input.value !== input.defaultValue) submitClean(form);
        });
      });
    }

    var inertBranches = [];
    function setBackgroundInert(enabled) {
      if (!enabled) {
        inertBranches.forEach(function (item) { item.element.inert = item.previous; });
        inertBranches = [];
        return;
      }
      var branch = panel;
      while (branch && branch.parentElement) {
        Array.prototype.forEach.call(branch.parentElement.children, function (sibling) {
          if (sibling === branch || sibling === backdrop || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) return;
          inertBranches.push({ element: sibling, previous: sibling.inert });
          sibling.inert = true;
        });
        branch = branch.parentElement;
        if (branch === document.body) break;
      }
    }
    function syncPanelAccess() {
      if (panel) panel.inert = !desktop.matches && !panel.classList.contains('is-open');
    }
    syncPanelAccess();

    function openPanel() {
      if (!panel || panel.classList.contains('is-open')) return;
      panel.classList.add('is-open');
      panel.inert = false;
      setBackgroundInert(true);
      if (backdrop) backdrop.hidden = false;
      document.documentElement.classList.add('cro-scroll-lock');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      var close = panel.querySelector('.cro-col__filters-close');
      if (close) window.setTimeout(function () { if (panel.classList.contains('is-open')) close.focus(); }, 50);
    }
    function closePanel() {
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      setBackgroundInert(false);
      syncPanelAccess();
      if (backdrop) backdrop.hidden = true;
      document.documentElement.classList.remove('cro-scroll-lock');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      if (openBtn) { openBtn.setAttribute('aria-expanded', 'false'); if (!desktop.matches) openBtn.focus(); }
    }
    if (openBtn) openBtn.addEventListener('click', openPanel);
    col.querySelectorAll('[data-cro-filters-close]').forEach(function (el) { el.addEventListener('click', closePanel); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab' && panel && panel.classList.contains('is-open')) {
        var items = focusables(panel);
        var first = items[0], last = items[items.length - 1];
        if (!first) { e.preventDefault(); return; }
        if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          e.preventDefault(); first.focus();
        }
      }
      if (e.key !== 'Escape') return;
      closePanel();
      closeQuickAdd();
    });
    if (desktop.addEventListener) desktop.addEventListener('change', function (e) { if (e.matches) closePanel(); syncPanelAccess(); });

    // Quick add size picker
    function closeQuickAdd(except) {
      col.querySelectorAll('[data-cro-qa-toggle][aria-expanded="true"]').forEach(function (btn) {
        if (btn === except) return;
        btn.setAttribute('aria-expanded', 'false');
        var list = document.getElementById(btn.getAttribute('aria-controls'));
        if (list) list.hidden = true;
        btn.closest('.cro-card').classList.remove('is-qa-open');
      });
    }
    col.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-cro-qa-toggle]');
      if (toggle) {
        e.preventDefault();
        var open = toggle.getAttribute('aria-expanded') === 'true';
        closeQuickAdd(toggle);
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        var list = document.getElementById(toggle.getAttribute('aria-controls'));
        if (list) list.hidden = open;
        toggle.closest('.cro-card').classList.toggle('is-qa-open', !open);
        if (!open && list) {
          var first = list.querySelector('button:not([disabled])');
          if (first) first.focus();
        }
        return;
      }
      if (!e.target.closest('[data-cro-qa]')) closeQuickAdd();
    });
    document.addEventListener('cro:cart-added', function () { closeQuickAdd(); });


  }

  ready(function () {
    initDrawer();
    initRecs();
    initPdp();
    initCollection();
  });

  window.CRO = window.CRO || {};
  window.CRO.ready = ready;
})();
